import produce from 'immer';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import type { IConversationMessage, IOroData, ISmsFormData } from '../algebra/conversations';
import useObservable from '../hooks/useObservable';
import { removeCookies } from '../prelude/constants';

/**
 *
 *! Parameters Used
   @started  : When hotlines conversation is started
   @anonymous : Represents the identity (Twilio Task ID)
   @messages : Chat Messages Bubble
   @isTyping : To show is typing bubble
   @id  : Random UUIDV4 generated
   @ended : Hotlines conversation ended.
   @captcha : Captcha code.
   @turn_arrived_in_hotlines : used when staffer send the first message ?
   @helproom_position_available : Used when Turn came in helprooms, It may happen user needs to wait in helproom till turn comes
   @helproom_opted : Called when we click on Opt In.
   @cancelled: Called when user cancels the task.
 */
export interface ISurvivorsConversation {
  started: boolean;
  anonymous: string;
  messages: Array<IConversationMessage>;
  isTyping: string;
  id: string;
  ended: boolean;
  captcha: string;
  messaging_service_key: string;
  helproom_position_available: boolean;
  turn_arrived_in_hotlines: boolean;
  helproom_opted: boolean;
  visitor_id: string;
  helproom_id: string;
  helproom_completed: boolean;
  cancelled: boolean;
  waiting_via_sms: boolean;
  persist_messages: Array<IConversationMessage>;
  userQuickExit: boolean;
  oroData: IOroData;
  sessionId: string;
  smsFormData: ISmsFormData;
}

const initialState: ISurvivorsConversation = {
  anonymous: '',
  started: false,
  messages: [],
  isTyping: '',
  id: '',
  ended: false,
  helproom_position_available: false,
  captcha: '',
  turn_arrived_in_hotlines: false,
  helproom_opted: false,
  visitor_id: '',
  helproom_id: '',
  messaging_service_key: '',
  helproom_completed: false,
  cancelled: false,
  waiting_via_sms: false,
  persist_messages: [],
  userQuickExit: false,
  oroData: {
    id: '',
    current_status: '',
    form_values: {},
    task_id: '',
    task_status: '',
  },
  sessionId: '',
  smsFormData: {
    task_sid: '',
    form_id: '',
    link: '',
    channelType: 'SMS',
  },
};

const survivorsConv$ = new BehaviorSubject(initialState);
export const SurvivorsConversationStore = {
  /**
   * Updating Messages -
   * @param message
   */
  updateMessages: (message: IConversationMessage): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.started = !(draftState.helproom_opted && draftState.helproom_position_available);
      draftState.turn_arrived_in_hotlines = !draftState.started ? draftState.messages.length === 0 : false;
      if (!draftState.helproom_opted || (draftState.helproom_opted && !draftState.helproom_position_available)) {
        if (!draftState.messages) {
          draftState.messages = [];
        }
        draftState.messages.push(message);
      }
      if (draftState.helproom_opted && draftState.helproom_position_available) {
        if (!draftState.persist_messages) {
          draftState.persist_messages = [];
        }
        draftState.persist_messages.push(message);
      }
    });
    survivorsConv$.next(state);
  },
  restoreSession: (messages: IConversationMessage[]) => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.messages = messages;
    });
    survivorsConv$.next(state);
  },
  /**
   *To be called Once Clicked on Yes, go to helprom
   */
  startHotlines: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.started = true;
    });
    survivorsConv$.next(state);
  },
  clearMessages: (): void => {
    const state = produce(survivorsConv$.value, () => initialState);
    survivorsConv$.next(state);
  },
  /**
   * Update survivor ID
   * @param task_sid
   */
  setSurvivorIdAndMessagingServiceKey: (task_sid: string, messaging_service_key: string): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.anonymous = `${task_sid}`;
      draftState.id = task_sid;
      draftState.messaging_service_key = messaging_service_key;
    });
    survivorsConv$.next(state);
  },

  /**
   * isTyping
   */
  setIsTyping: (isTyping: string): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.isTyping = isTyping;
    });
    survivorsConv$.next(state);
  },
  /**
   * Ending conversation.
   */
  endConversation: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      removeCookies(draftState.messaging_service_key);
      draftState.ended = true;
    });
    survivorsConv$.next(state);
  },
  cancelConversation: (): void => {
    const state = produce(initialState, (draftState) => {
      removeCookies(draftState.messaging_service_key);
      draftState.cancelled = true;
    });
    survivorsConv$.next(state);
  },
  /**
   * helproom Position Available
   */
  helproomPositionAvailable: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.helproom_position_available = true;
    });
    survivorsConv$.next(state);
  },
  /**
   *Helproom Opted
   */
  setHelproomOpted: ({ id, helproom_id }: { id: string; helproom_id: string }): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.helproom_opted = true;
      draftState.visitor_id = id;
      draftState.helproom_id = helproom_id;
    });
    survivorsConv$.next(state);
  },
  /**
   *
   * @param key
   */
  setHelproomMessagingServiceKey: (key: string): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.messaging_service_key = key;
    });
    survivorsConv$.next(state);
  },
  /**
   * This gets called when Opted out
   */
  optedOutHelproom: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.helproom_opted = false;
      draftState.visitor_id = '';
      draftState.helproom_id = '';
      draftState.helproom_position_available = false;
      draftState.helproom_completed = true;
    });
    survivorsConv$.next(state);
  },
  /**
   *! Called when we go to hotlines when turn arrives
   */
  goToHotlines: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.helproom_opted = false;
      draftState.visitor_id = '';
      draftState.helproom_id = '';
      draftState.helproom_position_available = false;
      draftState.helproom_completed = true;
      draftState.messages = draftState.persist_messages;
      draftState.started = true;
    });
    survivorsConv$.next(state);
  },
  /**
   *defer hotlines
   */
  deferHotlines: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.messages = [];
    });
    survivorsConv$.next(state);
  },
  stayedInHelproom: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.turn_arrived_in_hotlines = false;
      draftState.persist_messages = [];
    });
    survivorsConv$.next(state);
  },
  postHelproomFormCompletion: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.helproom_completed = false;
    });
    survivorsConv$.next(state);
  },
  waitingViaSMS: (waiting_via_sms: boolean): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.waiting_via_sms = waiting_via_sms;
    });
    survivorsConv$.next(state);
  },
  userQuickExit: (): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.userQuickExit = true;
    });
    survivorsConv$.next(state);
  },
  updateOroData: (data: IOroData): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.oroData = data;
    });
    survivorsConv$.next(state);
  },
  updateSmsFormData: (data: ISmsFormData) => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.smsFormData = data;
    });
    survivorsConv$.next(state);
  },
  storeSessionId: (sessionid: string): void => {
    const state = produce(survivorsConv$.value, (draftState) => {
      draftState.sessionId = sessionid;
    });
    survivorsConv$.next(state);
  },
};

/**
 * Hook Usage everywhere.
 */

export default function useGetSurvivorsConversation(): ISurvivorsConversation {
  return useObservable<ISurvivorsConversation>(survivorsConv$, survivorsConv$.value);
}
